import { useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { IconButton } from '@carbon/react';
import { Edit } from '@carbon/react/icons';
import classNames from 'classnames';
import { colormapStops, getColormap } from 'components/colormaps';
import { SortableList } from 'components/sortable-list/sortable-list';
import {
  colorByEditingRuleIdAtom,
  colorByInitialGroupIdAtom,
  colorByModalOpenAtom,
} from '../atoms';
import ruleStyles from '../color-by-section.module.scss';
import {
  selectedSubsurfaceMapAtom,
  subsurfaceColorRuleAtom,
  subsurfaceColoringEnabledAtom,
} from './atoms';
import { newColorRule, onlyCustomStops, ruleStops } from './color-rule';
import { SUBSURFACE_COLOR_GROUP_ID } from './color-tab';
import { useReadPooledRange } from './hooks';
import { SubsurfaceMapLegend } from './legend';
import { RampEditorPopover, sameStops } from './ramp-editor-popover';
import { defaultRampStops } from './utils';
import styles from './color-section.module.scss';

// The dataset has one colour rule (the ramp), so the card list holds one entry.
const RAMP_CARD = [{ id: 'ramp' }];
const keepOrder = () => {};

/**
 * The Petrotech Maps colour card in the left panel, drawn as the other datasets'
 * rule cards are (grip, name, Disable, pencil): the ramp swatch, Enable/Disable,
 * the pencil to the shared modal's Petrotech Maps tab, and the thin legend.
 * Colour changes are session-only in Epic 1.
 * @return {JSX.Element} The colour card.
 */
export const SubsurfaceColorSection = () => {
  const readPooledRange = useReadPooledRange();
  const [rule, setRule] = useAtom(subsurfaceColorRuleAtom);
  const [coloringEnabled, setColoringEnabled] = useAtom(
    subsurfaceColoringEnabledAtom
  );
  const shown = useAtomValue(selectedSubsurfaceMapAtom);
  const setModalOpen = useSetAtom(colorByModalOpenAtom);
  const setEditingRuleId = useSetAtom(colorByEditingRuleIdAtom);
  const setInitialGroupId = useSetAtom(colorByInitialGroupIdAtom);

  // The editor's Reset base: the rule's own named ramp, as the modal's editor uses.
  // A rule that holds nothing but custom stops resets to no rule at all, so its base
  // (and the Reset label) is the selected map's own default ramp, as with no rule.
  const defaultRamp = shown?.defaultColorRamp ?? null;
  const ownRamp = rule && !onlyCustomStops(rule) ? rule : null;
  const ruleRampId = ownRamp?.rampId;
  const ruleReversed = ownRamp?.reversed ?? false;
  const baseStops = useMemo(() => {
    if (ruleRampId) return colormapStops(ruleRampId, ruleReversed);
    return defaultRampStops(defaultRamp);
  }, [ruleRampId, ruleReversed, defaultRamp]);
  const stops = rule ? ruleStops(rule) : baseStops;
  const baseLabel = getColormap(ruleRampId ?? defaultRamp?.rampId).label;

  let coloringTitle = 'Select a Petrotech map to color it';
  if (shown)
    coloringTitle = coloringEnabled ? 'Disable coloring' : 'Enable coloring';
  // Struck through like a disabled categorical rule: listed, but not painting.
  const struck = !!shown && !coloringEnabled;
  // Named for what the ramp colours: the selected map's type ("Thickness"), or
  // "Map Values" until a map is selected.
  const ruleName = shown?.mapType || 'Map Values';

  // data-color-legend: the sidebar draws its divider under a colour area that
  // holds rules, as it does under the other datasets' rule cards.
  return (
    <div data-color-legend>
      <SortableList
        items={RAMP_CARD}
        getItemKey={(card) => card.id}
        onReorder={keepOrder}
        renderItem={() => (
          <div className={ruleStyles.rule}>
            <div className={ruleStyles.ruleHeader}>
              <span className={styles.title}>
                <RampEditorPopover
                  title="Petrotech Map"
                  stops={stops}
                  baseStops={baseStops}
                  baseLabel={baseLabel}
                  onChange={(nextStops) => {
                    // Back to the base ramp (Reset): drop the custom stops, and the
                    // whole rule when the stops were all it held, so the label,
                    // Reverse and the paint return to exactly where they were.
                    if (sameStops(nextStops, baseStops)) {
                      if (rule)
                        setRule(
                          onlyCustomStops(rule)
                            ? null
                            : { ...rule, customStops: null }
                        );
                      return;
                    }
                    setRule({
                      ...(rule ?? newColorRule(readPooledRange())),
                      customStops: nextStops,
                    });
                  }}
                />
                <span
                  className={classNames(ruleStyles.ruleName, {
                    [ruleStyles.ruleNameDisabled]: struck,
                  })}
                >
                  {ruleName}
                </span>
              </span>
              <div className={ruleStyles.ruleActions}>
                <button
                  type="button"
                  className={classNames(
                    ruleStyles.disableToggle,
                    styles.toggle
                  )}
                  disabled={!shown}
                  title={coloringTitle}
                  onClick={() => setColoringEnabled((enabled) => !enabled)}
                >
                  {shown && coloringEnabled ? 'Disable' : 'Enable'}
                </button>
                <IconButton
                  kind="ghost"
                  size="sm"
                  align="left"
                  autoAlign
                  label="Edit color"
                  onClick={() => {
                    setEditingRuleId(null);
                    setInitialGroupId(SUBSURFACE_COLOR_GROUP_ID);
                    setModalOpen(true);
                  }}
                >
                  <Edit />
                </IconButton>
              </div>
            </div>
            <SubsurfaceMapLegend />
          </div>
        )}
      />
    </div>
  );
};
